import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Check, Loader2, Gift, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useUserData } from './UserDataContext';
import { validateInviteUsername, saveInviteUsername, getInviteReward } from '../services/inviteService';

const MAX_SLOTS = 5;
const REWARD_GOLD = 500;

type SlotState = 'empty' | 'typing' | 'validating' | 'valid' | 'error';

interface Slot {
  value: string;
  state: SlotState;
  error?: string;
}

function makeSlot(): Slot {
  return { value: '', state: 'empty' };
}

interface InviteFriendsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRewarded: () => void;
}

export function InviteFriendsModal({ isOpen, onClose, onRewarded }: InviteFriendsModalProps) {
  const { userProfile, updateProfile } = useUserData();
  const [slots, setSlots] = useState<Slot[]>(Array.from({ length: MAX_SLOTS }, makeSlot));
  const [confirmedUsernames, setConfirmedUsernames] = useState<string[]>([]);
  const [celebrating, setCelebrating] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Load existing progress on open
  useEffect(() => {
    if (!isOpen || !userProfile?.id) return;
    const load = async () => {
      const reward = await getInviteReward(String(userProfile.id));
      if (reward?.invitedUsernames?.length) {
        const confirmed = reward.invitedUsernames;
        setConfirmedUsernames(confirmed);
        const newSlots = Array.from({ length: MAX_SLOTS }, (_, i): Slot => {
          if (i < confirmed.length) {
            return { value: confirmed[i], state: 'valid' };
          }
          return makeSlot();
        });
        setSlots(newSlots);
      }
    };
    load();
  }, [isOpen, userProfile?.id]);

  if (!isOpen) return null;

  const validCount = slots.filter(s => s.state === 'valid').length;

  const updateSlot = (index: number, patch: Partial<Slot>) => {
    setSlots(prev => prev.map((s, i) => i === index ? { ...s, ...patch } : s));
  };

  const handleChange = (index: number, value: string) => {
    updateSlot(index, { value, state: value ? 'typing' : 'empty', error: undefined });
  };

  const handleValidate = async (index: number) => {
    const slot = slots[index];
    const raw = slot.value.trim();
    if (!raw || slot.state === 'valid') return;

    // Basic format check
    const clean = raw.startsWith('@') ? raw.slice(1) : raw;
    if (clean.length < 2) {
      updateSlot(index, { state: 'error', error: 'Username too short.' });
      return;
    }

    updateSlot(index, { state: 'validating', error: undefined });

    try {
      const result = await validateInviteUsername(
        raw,
        String(userProfile.id),
        (userProfile as any).createdAt,
        confirmedUsernames
      );

      if (!result.ok) {
        updateSlot(index, { state: 'error', error: result.error });
        return;
      }

      // Valid — save to Firestore
      setLoading(true);
      const displayName = raw.startsWith('@') ? raw : `@${raw}`;
      const { rewarded } = await saveInviteUsername(
        String(userProfile.id),
        raw,
        confirmedUsernames
      );

      const newConfirmed = [...confirmedUsernames, displayName.toLowerCase()];
      setConfirmedUsernames(newConfirmed);
      updateSlot(index, { value: displayName, state: 'valid' });

      if (rewarded) {
        // Update local gold optimistically
        updateProfile({ gold: (userProfile.gold || 0) + REWARD_GOLD, goldBalance: (userProfile.goldBalance || 0) + REWARD_GOLD });
        setCelebrating(true);
        setTimeout(() => {
          setCelebrating(false);
          onRewarded();
          onClose();
        }, 3000);
      } else {
        toast.success(`✓ ${displayName} verified! ${MAX_SLOTS - newConfirmed.length} more to go.`);
        // Focus next empty slot
        const next = slots.findIndex((s, i) => i > index && s.state !== 'valid');
        if (next !== -1) setTimeout(() => inputRefs.current[next]?.focus(), 50);
      }
    } catch (err: any) {
      updateSlot(index, { state: 'error', error: err.message || 'Something went wrong.' });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleValidate(index);
    }
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          style={{ zIndex: 999999 }}
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            className="relative w-full max-w-md pointer-events-auto rounded-3xl overflow-hidden shadow-2xl"
            style={{ background: 'linear-gradient(160deg, #fffbef 0%, #fff8e1 100%)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Dark mode override */}
            <div className="hidden dark:block absolute inset-0 rounded-3xl" style={{ background: 'linear-gradient(160deg, #1a1a2e 0%, #16213e 100%)' }} />

            <div className="relative z-10 p-8">
                {/* Header */}
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg"
                      style={{ background: 'linear-gradient(135deg, #F39C12 0%, #FFD700 100%)' }}>
                      <Gift className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-[#1a1a1a] dark:text-white leading-tight">
                        Invite Friends
                      </h2>
                      <p className="text-sm font-semibold text-[#F39C12]">Earn 500 🪙 Gold</p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/5 text-[#666] dark:text-[#aaa] transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <p className="text-xs sm:text-sm text-[#666] dark:text-[#aaa] mb-6 leading-relaxed">
                  Enter the usernames of 5 of your friends who just joined AfroYap.
                  Once all 5 are verified, you'll receive <strong className="text-[#F39C12]">500 Gold!</strong>
                </p>

                {/* Progress bar */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-semibold text-[#666] dark:text-[#aaa]">Progress</span>
                    <span className="text-xs font-bold text-[#F39C12]">
                      {validCount} / {MAX_SLOTS} Invites Completed
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-black/10 dark:bg-white/10 overflow-hidden">
                    <motion.div
                      className="h-full rounded-full"
                      style={{ background: 'linear-gradient(90deg, #F39C12, #FFD700)' }}
                      animate={{ width: `${(validCount / MAX_SLOTS) * 100}%` }}
                      transition={{ duration: 0.4 }}
                    />
                  </div>
                </div>

                {/* Slot inputs */}
                <div className="space-y-4 mb-6">
                  {slots.map((slot, i) => {
                    const isLocked = slot.state === 'valid';
                    const isValidating = slot.state === 'validating';

                    return (
                      <div key={i}>
                        <div
                          className={`flex items-center gap-3 rounded-2xl px-4 py-3 border transition-all duration-200 ${
                            isLocked
                              ? 'bg-green-50 dark:bg-green-900/20 border-green-400 dark:border-green-600'
                              : slot.state === 'error'
                              ? 'bg-red-50 dark:bg-red-900/20 border-red-400 dark:border-red-600'
                              : 'bg-white/60 dark:bg-white/5 border-black/10 dark:border-white/10 focus-within:border-[#F39C12] dark:focus-within:border-[#4478ea]'
                          }`}
                        >
                          {/* Slot number / check */}
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-xs font-bold transition-colors ${
                            isLocked
                              ? 'bg-green-500 text-white'
                              : slot.state === 'error'
                              ? 'bg-red-400 text-white'
                              : 'bg-black/10 dark:bg-white/10 text-[#666] dark:text-[#aaa]'
                          }`}>
                            {isLocked ? <Check className="w-3.5 h-3.5" /> : i + 1}
                          </div>

                          {/* Input */}
                          <input
                            ref={el => { inputRefs.current[i] = el; }}
                            type="text"
                            placeholder={`@username ${i + 1}`}
                            value={slot.value}
                            disabled={isLocked || isValidating || loading}
                            onChange={e => handleChange(i, e.target.value)}
                            onKeyDown={e => handleKeyDown(i, e)}
                            onBlur={() => { if (slot.value.trim() && !isLocked) handleValidate(i); }}
                            className={`flex-1 bg-transparent outline-none text-sm font-medium placeholder:text-[#bbb] dark:placeholder:text-[#555] ${
                              isLocked
                                ? 'text-green-700 dark:text-green-400 line-through-none'
                                : slot.state === 'error'
                                ? 'text-red-600 dark:text-red-400'
                                : 'text-[#1a1a1a] dark:text-white'
                            }`}
                            autoComplete="off"
                            autoCapitalize="none"
                          />

                          {/* Right icon */}
                          {isValidating ? (
                            <Loader2 className="w-4 h-4 animate-spin text-[#F39C12]" />
                          ) : isLocked ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : slot.value.trim() && slot.state !== 'error' ? (
                            <button
                              onClick={() => handleValidate(i)}
                              disabled={loading}
                              className="text-[9px] font-bold px-2 py-0.5 rounded-full text-white shrink-0"
                              style={{ background: 'linear-gradient(90deg, #F39C12, #e67e22)' }}
                            >
                              Add
                            </button>
                          ) : null}
                        </div>

                        {/* Error message */}
                        <AnimatePresence>
                          {slot.state === 'error' && slot.error && (
                            <motion.p
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="text-red-500 text-xs mt-1 ml-10"
                            >
                              {slot.error}
                            </motion.p>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>
            </div>
          </motion.div>
        </motion.div>
      )}
      </AnimatePresence>

      {/* Celebration overlay */}
      <AnimatePresence>
        {celebrating && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex flex-col items-center justify-center bg-black/70 backdrop-blur-md pointer-events-auto"
            style={{ zIndex: 999999 }}
          >
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 250, damping: 18 }}
              className="text-center px-8"
            >
              <div className="text-7xl mb-6">🎉</div>
              <h2 className="text-3xl font-bold text-white mb-3">Congratulations!</h2>
              <p className="text-lg text-[#FFD700] font-semibold mb-1">
                You earned <strong>500 Gold</strong>!
              </p>
              <p className="text-sm text-white/60">Gold has been added to your account.</p>

              {/* Animated gold coins */}
              {Array.from({ length: 8 }).map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute text-3xl pointer-events-none"
                  initial={{ y: 0, x: 0, opacity: 1 }}
                  animate={{
                    y: -200 - Math.random() * 200,
                    x: (Math.random() - 0.5) * 400,
                    opacity: 0,
                    rotate: Math.random() * 360,
                  }}
                  transition={{ duration: 1.5 + Math.random(), delay: i * 0.1 }}
                  style={{
                    left: '50%',
                    top: '50%',
                  }}
                >
                  🪙
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
